import React from 'react';
import { Code, Zap, Palette, Database, Sparkles } from 'lucide-react';

interface Template {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  code: string;
  type: 'react' | 'nodejs';
}

const templates: Template[] = [
  {
    id: 'hello-world',
    name: 'Hello World',
    description: 'Simple greeting component',
    icon: <Code className="w-4 h-4" />,
    type: 'react',
    code: `function Component() {
  return (
    <div className="p-8 text-center">
      <h1 className="text-3xl font-bold text-blue-600 mb-4">
        Hello, React!
      </h1>
      <p className="text-gray-600">
        Welcome to the React Code Editor
      </p>
    </div>
  );
}`
  },
  {
    id: 'counter',
    name: 'Counter App',
    description: 'Interactive counter with state',
    icon: <Zap className="w-4 h-4" />,
    type: 'react',
    code: `function Component() {
  const [count, setCount] = useState(0);

  return (
    <div className="p-8 text-center">
      <h2 className="text-2xl font-bold mb-6">Counter: {count}</h2>
      <div className="space-x-4">
        <button
          onClick={() => setCount(count - 1)}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Decrease
        </button>
        <button
          onClick={() => setCount(count + 1)}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
        >
          Increase
        </button>
      </div>
    </div>
  );
}`
  },
  {
    id: 'color-picker',
    name: 'Color Picker',
    description: 'Interactive color selection',
    icon: <Palette className="w-4 h-4" />,
    type: 'react',
    code: `function Component() {
  const [color, setColor] = useState('#3b82f6');
  
  const colors = [
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'
  ];

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold mb-6">Color Picker</h2>
      <div 
        className="w-32 h-32 rounded-lg border-4 border-gray-300 mb-6 mx-auto transition-colors"
        style={{ backgroundColor: color }}
      />
      <div className="grid grid-cols-4 gap-3 max-w-xs mx-auto">
        {colors.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            className="w-12 h-12 rounded-lg border-2 border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <p className="text-center mt-4 font-mono text-sm text-gray-600">
        Selected: {color}
      </p>
    </div>
  );
}`
  },
  {
    id: 'todo-list',
    name: 'Todo List',
    description: 'Simple task management',
    icon: <Database className="w-4 h-4" />,
    type: 'react',
    code: `function Component() {
  const [todos, setTodos] = useState([
    { id: 1, text: 'Learn React', completed: true },
    { id: 2, text: 'Build awesome apps', completed: false }
  ]);
  const [input, setInput] = useState('');

  const addTodo = () => {
    if (input.trim()) {
      setTodos([...todos, {
        id: Date.now(),
        text: input,
        completed: false
      }]);
      setInput('');
    }
  };

  const toggleTodo = (id) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  return (
    <div className="p-8 max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-6">Todo List</h2>
      <div className="flex mb-4">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addTodo()}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Add a new todo..."
        />
        <button
          onClick={addTodo}
          className="px-4 py-2 bg-blue-500 text-white rounded-r hover:bg-blue-600 transition-colors"
        >
          Add
        </button>
      </div>
      <ul className="space-y-2">
        {todos.map((todo) => (
          <li
            key={todo.id}
            className="flex items-center p-3 bg-gray-50 rounded"
          >
            <input
              type="checkbox"
              checked={todo.completed}
              onChange={() => toggleTodo(todo.id)}
              className="mr-3"
            />
            <span className={todo.completed ? 'line-through text-gray-500' : ''}>
              {todo.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}`
  },
  {
    id: 'svelte-counter',
    name: 'Svelte Counter',
    description: 'Svelte-style reactive counter',
    icon: <Sparkles className="w-4 h-4" />,
    type: 'react',
    code: `function Component() {
  const [count, setCount] = useState(0);
  const [doubled, setDoubled] = useState(0);

  // Svelte-style reactive statement
  useEffect(() => {
    setDoubled(count * 2);
  }, [count]);

  return (
    <div className="p-8 text-center">
      <h2 className="text-2xl font-bold mb-6">Svelte-Style Counter</h2>
      <div className="space-y-4">
        <div className="text-4xl font-bold text-blue-600">{count}</div>
        <div className="text-lg text-gray-600">Doubled: {doubled}</div>
        <div className="space-x-4">
          <button
            onClick={() => setCount(count - 1)}
            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
          >
            -1
          </button>
          <button
            onClick={() => setCount(0)}
            className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            Reset
          </button>
          <button
            onClick={() => setCount(count + 1)}
            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
          >
            +1
          </button>
        </div>
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600">
            This demonstrates Svelte-style reactivity where the doubled value automatically updates when count changes.
          </p>
        </div>
      </div>
    </div>
  );
}`
  }
];

interface ExampleTemplatesProps {
  onSelectTemplate: (code: string, type: 'react' | 'nodejs') => void;
}

export default function ExampleTemplates({ onSelectTemplate }: ExampleTemplatesProps) {
  return (
    <div className="p-4 bg-gray-50 border-b border-gray-200">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Start Templates</h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {templates.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelectTemplate(template.code, template.type)}
            className="p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors group"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-md mx-auto mb-2 bg-gray-100 group-hover:bg-blue-100">
              {template.icon}
            </div>
            <div className="text-xs font-medium text-gray-700 group-hover:text-blue-700">
              {template.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}